import { motion } from "framer-motion";
import {
  FileText,
  Search,
  Package,
  Users,
  CreditCard,
  Shield,
  Calendar,
  AlertCircle,
  CheckCircle,
  Info,
  Heart,
  Star,
  Zap,
  Target,
  TrendingUp,
} from "lucide-react";

const ICON_MAP = {
  // Default icons
  default: FileText,
  search: Search,
  package: Package,
  users: Users,
  payment: CreditCard,
  policy: Shield,
  calendar: Calendar,
  error: AlertCircle,
  success: CheckCircle,
  info: Info,
  heart: Heart,
  star: Star,
  zap: Zap,
  target: Target,
  trending: TrendingUp,
};

const COLOR_VARIANTS = {
  default: {
    icon: "text-emerald-400",
    bg: "bg-emerald-100",
    title: "text-gray-900",
    description: "text-gray-600",
    button: "bg-emerald-600 hover:bg-emerald-700",
  },
  primary: {
    icon: "text-emerald-500",
    bg: "bg-emerald-100",
    title: "text-gray-900",
    description: "text-gray-600",
    button: "bg-emerald-600 hover:bg-emerald-700",
  },
  success: {
    icon: "text-green-500",
    bg: "bg-green-100",
    title: "text-gray-900",
    description: "text-gray-600",
    button: "bg-green-600 hover:bg-green-700",
  },
  warning: {
    icon: "text-lime-500",
    bg: "bg-lime-100",
    title: "text-gray-900",
    description: "text-gray-600",
    button: "bg-lime-600 hover:bg-lime-700",
  },
  error: {
    icon: "text-red-400",
    bg: "bg-red-100",
    title: "text-gray-900",
    description: "text-gray-600",
    button: "bg-red-600 hover:bg-red-700",
  },
};

export default function EmptyState({
  // Content
  title = "No data available",
  description = "There's nothing to show here yet.",
  subtitle = "", // Custom text under icon
  
  // Visual
  icon = "default",
  variant = "default",
  size = "md",
  
  // Actions
  actionLabel,
  onAction,
  
  // Layout
  className = "",
  showIcon = true,
  centered = true,
}) {
  const IconComponent = ICON_MAP[icon] || ICON_MAP.default;
  const colors = COLOR_VARIANTS[variant] || COLOR_VARIANTS.default;

  const sizeClasses = {
    sm: {
      container: "py-8",
      icon: "w-12 h-12",
      iconSize: 24,
      title: "text-lg",
      description: "text-sm",
      button: "px-4 py-2 text-sm",
    },
    md: {
      container: "py-12",
      icon: "w-16 h-16",
      iconSize: 32,
      title: "text-xl",
      description: "text-base",
      button: "px-6 py-3 text-base",
    },
    lg: {
      container: "py-16",
      icon: "w-20 h-20",
      iconSize: 40,
      title: "text-2xl",
      description: "text-lg",
      button: "px-8 py-4 text-lg",
    },
  };

  const currentSize = sizeClasses[size] || sizeClasses.md;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`${
        centered ? "flex flex-col items-center justify-center" : ""
      } ${currentSize.container} ${className}`}
    >
      {/* Icon */}
      {showIcon && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
          className={`${currentSize.icon} ${colors.bg} rounded-full flex items-center justify-center mb-6`}
        >
          <IconComponent size={currentSize.iconSize} className={colors.icon} />
        </motion.div>
      )}

      {/* Subtitle under icon */}
      {subtitle && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className={`text-sm font-medium ${colors.icon} mb-4`}
        >
          {subtitle}
        </motion.p>
      )}

      {/* Content */}
      <div className={`text-center max-w-md ${!centered ? "text-left" : ""}`}>
        <motion.h3
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className={`${currentSize.title} font-semibold ${colors.title} mb-3`}
        >
          {title}
        </motion.h3>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className={`${currentSize.description} ${colors.description} mb-6`}
        >
          {description}
        </motion.p>

        {/* Action Button */}
        {actionLabel && onAction && (
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            onClick={onAction}
            className={`${currentSize.button} font-semibold text-white rounded-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${colors.button}`}
          >
            {actionLabel}
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

// Preset configurations for common use cases
export const EmptyStatePresets = {
  // Policy related
  noPolicies: {
    icon: "policy",
    variant: "primary",
    title: "No Purchased Policies Found",
    description:
      "You haven't purchased any insurance policies yet. Browse our available options to get started.",
    actionLabel: "Browse Policies",
  },

  // Search related
  noSearchResults: {
    icon: "search",
    variant: "default",
    title: "No Results Found",
    description:
      "We couldn't find anything matching your search criteria. Try adjusting your filters or search terms.",
    actionLabel: "Clear Filters",
  },

  // Payment related
  noPayments: {
    icon: "payment",
    variant: "default",
    title: "No Payment History",
    description:
      "You haven't made any payments yet. Your payment history will appear here once you make your first purchase.",
  },

  // User related
  noUsers: {
    icon: "users",
    variant: "default",
    title: "No Users Found",
    description: "There are no users to display at the moment.",
  },

  // Calendar related
  noEvents: {
    icon: "calendar",
    variant: "default",
    title: "No Events Scheduled",
    description:
      "You don't have any upcoming events. Create your first event to get started.",
    actionLabel: "Create Event",
  },

  // Success state
  success: {
    icon: "success",
    variant: "success",
    title: "All Done!",
    description: "You've completed all your tasks. Great job!",
  },

  // Error state
  error: {
    icon: "error",
    variant: "error",
    title: "Something Went Wrong",
    description:
      "We encountered an error while loading your data. Please try again.",
    actionLabel: "Try Again",
  },

  // Coming soon
  comingSoon: {
    icon: "zap",
    variant: "warning",
    title: "Coming Soon",
    description:
      "This feature is currently under development. Check back later for updates!",
  },
};
